import { observable, action, computed, reaction, makeObservable } from 'mobx';
import { debounce } from 'debounce';
import * as colors from '@mui/material/colors';
import EditorStore from './EditorStore';
import agent from '../lib/agent';
import {
  hexToRgb,
  elevateGeometry,
  getElevationFromGeometry
} from '../lib/utils';

class FeatureSettingsStore {
  id = null;
  properties = [];
  metadata = [];
  dataset = null;
  layer = null;
  name = null;
  height = 0;
  scale = 1;
  strokeWidth = 1;
  color = [0, 112, 255, 255];
  icon = null;
  layerId = null;
  isLoading = true;
  geometry = null;
  angle = 0;
  bbox = null;
  allColors = Object.entries(colors).reduce(
    (acc, [key, val]) => {
      if (key === 'common') return acc;
      if (key.split(/(?=[A-Z])/).length > 1) return acc;

      const label = key.charAt(0).toUpperCase() + key.slice(1);
      const value = val[400];
      const rgb = hexToRgb(value);
      if (rgb) {
        acc.push({
          label,
          value,
          rgb
        });
      }

      return acc;
    },
    [
      {
        label: 'Dark Blue (Default)',
        value: '#0070FF',
        rgb: hexToRgb('#0070FF')
      },
      {
        label: 'Orange (CHP)',
        value: '#ff6c00',
        rgb: hexToRgb('#ff6c00')
      },
      {
        label: 'Blue (Fuel Cell)',
        value: '#01A3FF',
        rgb: hexToRgb('#01A3FF')
      },
      {
        label: 'Green (Storage)',
        value: '#05D748',
        rgb: hexToRgb('#05D748')
      },
      {
        label: 'Yellow (Solar)',
        value: '#FFBC00',
        rgb: hexToRgb('#FFBC00')
      }
    ]
  );

  loadFeature = async (datasetId, featureId) => {
    this.isLoading = true;
    this.id = null;
    this.strokeWidth = 1;
    this.angle = 0;
    this.scale = 1;
    this.name = null;
    this.dataset = null;
    this.geometry = null;
    this.icon = null;
    this.layer = null;
    this.color = [0, 112, 255, 255];
    this.height = 1;
    this.layerId = null;
    this.layer = null;
    this.bbox = null;
    this.properties = [];
    this.metadata = [];

    if (datasetId === null) {
      this.isLoading = false;
      return;
    }

    const response = await agent.Dataset.getFeature(datasetId, featureId);
    const feature = response.data?.data;
    if (!feature) {
      console.error(`No Feature Found`);
      return;
    }

    this.dataset = feature.dataset;
    this.layer = feature.layer;
    this.bbox = feature.bbox;
    this.name = feature.name;
    this.layer = feature.layer;
    this.icon = feature.icon;
    this.strokeWidth = feature.stroke_width;
    this.angle = feature.angle || 0;
    this.scale = feature.scale || 1;

    this.height = feature.height || 0;
    if (feature.color) this.color = feature.color;
    if (EditorStore.currentDataset?.id !== feature.dataset?.id) {
      EditorStore.currentDataset = feature.dataset;
    }
    if (EditorStore.currentLayer?.id !== feature.layer?.id) {
      EditorStore.currentLayer = feature.layer;
    }

    this.geometry = feature.geometry;

    this.properties = Object.entries(feature.properties).reduce(
      (acc, kv, idx) => {
        const [name, value] = kv;
        const id = `${idx + 1}`;
        acc.push({
          id,
          name,
          value
        });
        return acc;
      },
      []
    );

    if (feature.properties.project_id) {
      this.projectId = feature.properties.project_id;
    }

    this.id = feature.id;
    this.layerId = feature.layer_id;
    // this.setProperties(properties);
    this.isLoading = false;
    return response.data.data;
  };

  constructor() {
    makeObservable(this, {
      id: observable,
      properties: observable,
      metadata: observable,
      dataset: observable,
      layer: observable,
      name: observable,
      height: observable,
      scale: observable,
      strokeWidth: observable,
      angle: observable,
      color: observable,
      icon: observable,
      layerId: observable,
      isLoading: observable,
      geometry: observable,
      bbox: observable,
      loadFeature: action,
      setProperty: action,
      addProperty: action,
      setLayer: action,
      setHeight: action,
      setIcon: action,
      setOpacity: action,
      setStrokeWidth: action,
      setElevation: action,
      setName: action,
      setScale: action,
      setColor: action,
      isBuilding: computed,
      heightInFeet: computed,
      elevationInFeet: computed,
      selectedColor: computed,
      opacityPercentage: computed,
      elevation: computed,
      geoJSON: computed,
      update: action,
      deleteProperty: action,
      setGeometry: action,
      getProperties: action,
      deleteFeature: action,
      getId: action,
      duplicate: action
    });
  }

  setProperty(id, field, value) {
    const newProperties = [...this.properties];

    const idx = newProperties.findIndex(item => item.id === id);
    if (idx !== -1) {
      if (field === 'name') {
        newProperties[idx].name = value;
      } else if (field === 'value') {
        newProperties[idx].value = value;
      } else {
        newProperties[idx].value = value;
      }
    } else if (['_opacity'].includes(id)) {
      newProperties.push({
        value,
        id,
        name: id
      });
    }
    this.properties = newProperties;
  }

  addProperty = () => {
    const row = {
      id: `${this.properties.length + 2}`,
      name: '',
      val: ''
    };

    this.properties = [...this.properties, row];
  };

  setLayer = async layer => {
    this.layer = layer;
    // update layer immediately since layer feature count depends on it
    await this.update();
    EditorStore.getDatasetFeatures();
    EditorStore.getLayers();
  };

  setHeight = value => {
    this.height = value;
  };

  setIcon = value => {
    this.icon = value;
  };

  setOpacity = value => {
    this.color = [...this.color.slice(0, 3), value];
  };

  setStrokeWidth = value => {
    this.strokeWidth = value;
  };

  setElevation = value => {
    this.geometry = elevateGeometry(this.geometry, value);
  };

  setName = name => {
    this.name = name;
  };

  setScale = scale => {
    this.scale = scale;
  };

  setColor = value => {
    let rgb = value;
    if (value.startsWith('#')) {
      rgb = hexToRgb(value);
    }
    this.color = [...rgb, this.color[3]];
  };

  get isBuilding() {
    return this.id?.startsWith('pb_');
  }

  get heightInFeet() {
    return Math.round(this.height * 3.28084);
  }

  get elevationInFeet() {
    return Math.round(this.elevation * 3.28084);
  }

  get selectedColor() {
    const { color, allColors } = this;
    const item = allColors.find(c => color.slice(0, 3).join() === c.rgb.join());
    return item;
  }

  get opacityPercentage() {
    const value = Math.round((this.color[3] / 255) * 100);
    return value;
  }

  get elevation() {
    return getElevationFromGeometry(this.geometry);
  }

  get geoJSON() {
    const properties = [...this.properties].reduce((acc, prop) => {
      acc[prop.name] = prop.value;
      return acc;
    }, {});

    const feature = {
      id: this.id,
      name: this.name,
      height: this.height,
      color: this.color,
      layer_id: this.layer?.id,
      icon: this.icon,
      stroke_width: this.strokeWidth,
      angle: this.angle,
      scale: this.scale,
      geometry: this.geometry,
      properties
    };

    return feature;
  }

  update = async () => {
    const feature = this.geoJSON;

    try {
      const res = await agent.Dataset.updateFeature(this.dataset.id, feature);

      if (res.data?.data?.area_in_sq_meters) {
        const area = res.data.data.area_in_sq_meters;
        const areaInSqFeet = (area * 10.76391).toFixed(2);

        // const areaInSqMetersIndex = this.metadata.findIndex(
        //   item => item.label === 'area in sq meters'
        // );
        const areaInSqFeetIndex = this.metadata.findIndex(
          item => item.label === 'Area'
        );

        // if (areaInSqMetersIndex !== -1) {
        //   this.metadata[areaInSqMetersIndex].value = area;
        // } else {
        //   this.metadata.push({
        //     label: 'area in sq meters',
        //     value: area
        //   });
        // }

        if (areaInSqFeetIndex !== -1) {
          this.metadata[areaInSqFeetIndex].value = areaInSqFeet;
        } else {
          this.metadata.push({
            label: 'Area',
            value: `${areaInSqFeet} ft²`
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  deleteProperty = itemId => {
    const newProperties = [...this.properties].filter(
      item => item.id !== itemId
    );
    this.properties = newProperties;
  };

  setGeometry = geometry => {
    this.geometry = geometry;
  };

  setAngle = angle => {
    this.angle = angle;
  };

  getProperties = () => {
    return this.properties.reduce((acc, val) => {
      acc[val.name] = val.value;
      return acc;
    }, {});
  };

  deleteFeature = async () => {
    // this.features.delete(featureId);
    // agent.Dataset.deleteFeature(this.dataset.id, this.id);
    await EditorStore.deleteFeature(this.id);
    this.loadFeature(null);
  };

  getId = () => {
    return this.id;
  };

  duplicate = async () => {
    const feature = this.geoJSON;
    const response = await agent.Dataset.createFeature(this.dataset.id, {
      ...feature,
      id: null
    });

    const { id } = response.data.data;
    return id;
  };
}

const store = new FeatureSettingsStore();
const debouncedUpdate = debounce(store.update, 200);

reaction(
  () =>
    JSON.stringify([
      store.color,
      store.icon,
      store.height,
      store.properties,
      store.strokeWidth,
      store.name,
      store.geometry,
      store.scale,
      store.angle
    ]),
  () => {
    if (store.id && !store.isLoading) debouncedUpdate();
  }
);

export default store;
